import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAppliedRadius } from 'site-modules/shared/utils/inventory/srp-utils';
import {
  getAllFacetsBySelectedValues,
  getFacetsBySelectedValues,
  getFilter,
} from 'site-modules/shared/utils/inventory/search-filter';
import { isEqual, omit } from 'lodash';
import { InventoryModel } from 'client/data/models/inventory';
import { VisitorModel } from 'client/data/models/visitor';
import { getFreshHistoryParam } from 'site-modules/shared/components/search-by-module/search-by-module-utils';

export function useCoreSrpFilter({
  inventoryData,
  facets,
  selectedFacets,
  pathname,
  visitorLocation,
  initialFilter,
  addFiltersToQuery,
  setModelValue,
}) {
  const [isLoading, setLoading] = useState(false);
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const selectedFiltersBeforeDrawer = useRef();
  const drawerFiltersCancelled = useRef(false);

  const radius = useMemo(() => getAppliedRadius({ inventoryData, visitorLocation, selectedFacets }), [
    inventoryData,
    selectedFacets,
    visitorLocation,
  ]);

  const filteringByFacets = useMemo(
    () => getFacetsBySelectedValues(getAllFacetsBySelectedValues(facets, selectedFacets)),
    [facets, selectedFacets]
  );

  const onUpdateFilter = useCallback(
    (type, facet, isChecked) => {
      setLoading(true);

      requestAnimationFrame(() => {
        setTimeout(() => {
          const updatedFilter = facet
            ? getFilter({
                type,
                facet,
                isChecked,
                currentFilter: selectedFacets,
                radius,
                facets,
              })
            : initialFilter;

          if (isEqual(updatedFilter, selectedFacets)) {
            setLoading(false);
            return;
          }

          setModelValue('searchResultsFilter', InventoryModel, updatedFilter);
          addFiltersToQuery(pathname, updatedFilter);
        }, 0);
      });
    },
    [addFiltersToQuery, facets, initialFilter, pathname, radius, selectedFacets, setModelValue]
  );

  const onFiltersButtonClick = useCallback(() => {
    selectedFiltersBeforeDrawer.current = selectedFacets;
    setTimeout(() => {
      setFilterDrawerOpen(true);
    }, 0);
  }, [selectedFacets]);

  const closeFiltersDrawer = useCallback(() => {
    setTimeout(() => {
      setFilterDrawerOpen(false);
    }, 0);
    selectedFiltersBeforeDrawer.current = null;
    drawerFiltersCancelled.current = false;
  }, []);

  const cancelFiltersUpdate = useCallback(() => {
    if (!isEqual(selectedFacets, selectedFiltersBeforeDrawer.current)) {
      setModelValue('searchResultsFilter', InventoryModel, selectedFiltersBeforeDrawer.current);
      drawerFiltersCancelled.current = true;
    }

    closeFiltersDrawer();
  }, [closeFiltersDrawer, selectedFacets, setModelValue]);

  useEffect(() => {
    setLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryData]);

  const updateZipCode = useCallback(
    async newZipCode => {
      setLoading(true);
      try {
        await setModelValue('location', VisitorModel, { zipCode: newZipCode });
        await setModelValue(
          'searchResultsFilter',
          InventoryModel,
          omit(
            {
              ...selectedFacets,
              ...getFreshHistoryParam(),
            },
            ['national']
          )
        );
      } catch (e) {
        setLoading(false);
      }
    },
    [selectedFacets, setModelValue]
  );

  return {
    filteringByFacets,
    radius,
    onUpdateFilter,
    onFiltersButtonClick,
    closeFiltersDrawer,
    cancelFiltersUpdate,
    updateZipCode,
    isFilterDrawerOpen,
    isLoading,
  };
}
